// arsenal-tpms/frontend/src/pages/SensorsPage.jsx (ACTUALIZADO)
import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Sensors as SensorIcon,
  ViewModule as CardViewIcon,
  TableChart as TableViewIcon,
  ViewList as CompactViewIcon,
  DirectionsCar as VehicleIcon,
  LocationOn as PositionIcon
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';

import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import SensorForm from '../components/sensors/SensorForm';
import AdvancedDataTable from '../components/common/AdvancedDataTable';

const safeFormatDateTime = (value) => {
  try {
    if (!value) return 'N/A';
    let date;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      date = new Date(value);
    } else {
      return 'Invalid Date';
    }
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return 'Error';
  }
};

const ensureString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (value && typeof value.then === 'function') {
    console.error('WARNING: Attempted to render a Promise!', value);
    return fallback;
  }
  if (typeof value === 'object') {
    console.error('WARNING: Attempted to render an object!', value);
    return fallback;
  }
  return fallback;
};

const findSensorAssignmentInVehicles = (vehicles, sensorId) => {
  if (!Array.isArray(vehicles)) return null;

  const visitVehicle = (vehicle) => {
    if (Array.isArray(vehicle.vehicle_sensors)) {
      const vehicleSensor = vehicle.vehicle_sensors.find(
        (vs) => vs.sensor_id === sensorId
      );
      if (vehicleSensor) {
        return { vehicle, vehicleSensor };
      }
    }
    if (Array.isArray(vehicle.child_vehicles) && vehicle.child_vehicles.length > 0) {
      for (const child of vehicle.child_vehicles) {
        const found = visitVehicle(child);
        if (found) return found;
      }
    }
    return null;
  };

  for (const v of vehicles) {
    const found = visitVehicle(v);
    if (found) return found;
  }
  return null;
};

const getErrorMessage = (error, fallback = 'Error desconocido') => {
  if (!error) return fallback;
  if (error.message && typeof error.message === 'string') {
    return error.message;
  }
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
  }
  if (error.detail && typeof error.detail === 'string') {
    return error.detail;
  }
  if (error.error && typeof error.error === 'string') {
    return error.error;
  }
  if (typeof error === 'string') {
    return error;
  }
  return fallback;
};

const SimpleLoading = ({ message = 'Cargando...' }) => (
  <Container maxWidth={false} disableGutters>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <CircularProgress />
      <Typography variant="h6" color="text.secondary">
        {message}
      </Typography>
    </Box>
  </Container>
);

// Card View Component
const SensorsCardView = ({ sensors, vehicles, onEdit, onDelete, t }) => {
  const getSensorAssignment = (sensor) => {
    const found = findSensorAssignmentInVehicles(vehicles, sensor.sensor_id);
    if (found) {
      return {
        vehicleName: ensureString(found.vehicle.display_name),
        position: ensureString(found.vehicleSensor?.position),
      };
    }
    return { vehicleName: null, position: null };
  };

  return (
    <Grid container spacing={3}>
      {sensors.map((sensor) => {
        const assignment = getSensorAssignment(sensor);
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={sensor.id}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <SensorIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap fontWeight={600}>
                      {ensureString(sensor.sensor_id)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2, minHeight: 60 }}>
                  {assignment.vehicleName ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <VehicleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {assignment.vehicleName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PositionIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" color="primary.main">
                          {t('sensors.table.position', 'Posición')} {assignment.position}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={t('sensors.card.unassigned', 'Sin Asignar')}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {t('sensors.card.created', 'Creado')}: {safeFormatDateTime(sensor.created_at)}
                </Typography>
              </CardContent>

              {(onEdit || onDelete) && (
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onEdit && (
                      <Tooltip title={t('sensors.actions.editSensor', 'Editar Sensor')}>
                        <IconButton size="small" onClick={() => onEdit(sensor)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title={t('sensors.actions.deleteSensor', 'Eliminar Sensor')}>
                        <IconButton size="small" color="error" onClick={() => onDelete(sensor)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              )}
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

// Compact List View Component
const CompactListView = ({ sensors, vehicles, onEdit, onDelete, hasPermission, t }) => {
  const getSensorAssignment = (sensor) => {
    const found = findSensorAssignmentInVehicles(vehicles, sensor.sensor_id);
    if (found) {
      return {
        vehicleName: ensureString(found.vehicle.display_name),
        position: ensureString(found.vehicleSensor?.position),
      };
    }
    return { vehicleName: null, position: null };
  };

  return (
    <Paper>
      <List>
        {sensors.map((sensor, index) => {
          const assignment = getSensorAssignment(sensor);
          return (
            <React.Fragment key={sensor.id}>
              <ListItem
                secondaryAction={
                  hasPermission('manager') && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {onEdit && (
                        <IconButton size="small" onClick={() => onEdit(sensor)}>
                          <EditIcon />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton size="small" color="error" onClick={() => onDelete(sensor)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  )
                }
              >
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <SensorIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {ensureString(sensor.sensor_id)}
                      </Typography>
                      {assignment.vehicleName ? (
                        <Chip label={t('sensors.card.assigned', 'Asignado')} size="small" color="success" />
                      ) : (
                        <Chip label={t('sensors.card.unassigned', 'Sin Asignar')} size="small" color="default" variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={
                    assignment.vehicleName ?
                      `${assignment.vehicleName} • ${t('sensors.table.position', 'Posición')} ${assignment.position}` :
                      t('sensors.card.unassigned', 'Sensor sin asignar a ningún vehículo')
                  }
                />
              </ListItem>
              {index < sensors.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Paper>
  );
};

const SensorsPage = () => {
  const theme = useTheme();
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { t: originalT, ready: i18nReady } = useTranslation();

  const t = (key, fallback = null) => {
    try {
      if (!i18nReady) {
        return fallback || key.split('.').pop() || key;
      }
      const result = originalT(key);
      if (result && typeof result === 'object' && typeof result.then === 'function') {
        return fallback || key.split('.').pop() || key;
      }
      if (typeof result === 'string') {
        return result;
      }
      return fallback || key.split('.').pop() || key;
    } catch (error) {
      return fallback || key.split('.').pop() || key;
    }
  };

  const [viewMode, setViewMode] = useState('table');
  const [searchSensors, setSearchSensors] = useState('');
  const [sensorDialog, setSensorDialog] = useState({ open: false, sensor: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sensor: null });
  const [orderBy] = useState('sensor_id');
  const [order] = useState('asc');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [savingSensors, setSavingSensors] = useState(false);

  // API calls
  const {
    data: sensors,
    loading: sensorsLoading,
    refetch: refetchSensors
  } = useApiCall(api.sensors.getAll);

  const {
    data: vehicles,
    loading: vehiclesLoading,
    refetch: refetchVehicles
  } = useApiCall(api.vehicles.getAll);

  // Mutations
  const { mutate: updateSensor, loading: updating } = useMutation(
    ({ id, data }) => api.sensors.update(id, data),
    {
      onSuccess: () => {
        showSuccess(t('sensors.actions.sensorUpdated', 'Sensor actualizado exitosamente'));
        setSensorDialog({ open: false, sensor: null });
        refetchSensors();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error, t('hardcoded.pages_sensorspage.error_al_actualizar_sensor'));
        showError(errorMessage);
      }
    }
  );

  const { mutate: deleteSensor, loading: deleting } = useMutation(api.sensors.delete, {
    onSuccess: () => {
      showSuccess(t('sensors.actions.sensorDeleted', 'Sensor eliminado exitosamente'));
      setDeleteDialog({ open: false, sensor: null });
      refetchSensors();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error, t('hardcoded.pages_sensorspage.error_al_eliminar_sensor'));
      showError(errorMessage);
    }
  });

  // Handle sensor dialog
  const handleSensorDialog = (sensor = null) => {
    setSensorDialog({ open: true, sensor });
  };

  const handleSensorSubmit = async (payload) => {
    if (sensorDialog.sensor) {
      updateSensor({ id: sensorDialog.sensor.id, data: payload });
      return null;
    }

    const sensorsToCreate = Array.isArray(payload?.sensors) ? payload.sensors : [payload];
    const vehicleId = payload?.vehicle_id || null;
    const createdSensors = [];

    setSavingSensors(true);
    try {
      for (const entry of sensorsToCreate) {
        const response = await api.sensors.create({ sensor_id: entry.sensor_id });
        const createdSensor = response.data;
        createdSensors.push(createdSensor);

        if (vehicleId && entry.position) {
          await api.vehicles.assignSensor(vehicleId, {
            sensor_id: createdSensor.id,
            position: entry.position,
          });
        }
      }

      showSuccess(
        createdSensors.length === 1
          ? t('sensors.actions.sensorCreated', 'Sensor registrado exitosamente')
          : `${createdSensors.length} sensores registrados exitosamente`
      );
      setSensorDialog({ open: false, sensor: null });
      await Promise.all([refetchSensors(), refetchVehicles()]);

      return { success: true, data: createdSensors };
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('hardcoded.pages_sensorspage.error_al_registrar_sensores'));
      showError(errorMessage);
      return { error: true, message: errorMessage };
    } finally {
      setSavingSensors(false);
    }
  };

  // Handle delete
  const handleDelete = (sensor) => {
    setDeleteDialog({ open: true, sensor });
  };

  const confirmDelete = () => {
    if (deleteDialog.sensor) {
      deleteSensor(deleteDialog.sensor.id);
    }
  };

  // Helper function to get vehicle and position info
  const getSensorAssignment = (sensor) => {
    const found = findSensorAssignmentInVehicles(vehicles, sensor.sensor_id);
    if (found) {
      return {
        vehicleName: ensureString(found.vehicle.display_name),
        position: ensureString(found.vehicleSensor?.position),
      };
    }
    return { vehicleName: null, position: null };
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue, bValue;

    if (orderBy === 'vehicle') {
      const aAssignment = getSensorAssignment(a);
      const bAssignment = getSensorAssignment(b);
      aValue = aAssignment.vehicleName || '';
      bValue = bAssignment.vehicleName || '';
    } else if (orderBy === 'position') {
      const aAssignment = getSensorAssignment(a);
      const bAssignment = getSensorAssignment(b);
      aValue = aAssignment.position || '';
      bValue = bAssignment.position || '';
    } else if (orderBy === 'created_at') {
      aValue = new Date(a.created_at).getTime();
      bValue = new Date(b.created_at).getTime();
    } else {
      aValue = a[orderBy] || '';
      bValue = b[orderBy] || '';
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  };

  // Filter and sort sensors
  let filteredAndSortedSensors = sensors?.filter(sensor =>
    sensor.sensor_id.toLowerCase().includes(searchSensors.toLowerCase())
  ) || [];

  if (assignmentFilter !== 'all') {
    filteredAndSortedSensors = filteredAndSortedSensors.filter((sensor) => {
      const assignment = getSensorAssignment(sensor);
      return assignmentFilter === 'assigned' ? !!assignment.vehicleName : !assignment.vehicleName;
    });
  }

  filteredAndSortedSensors = filteredAndSortedSensors.sort(getComparator(order, orderBy));

  // View mode options
  const viewModeOptions = [
    { value: 'table', label: t('hardcoded.pages_sensorspage.table_view', 'Tabla'), icon: <TableViewIcon /> },
    { value: 'cards', label: t('hardcoded.pages_sensorspage.cards_view', 'Tarjetas'), icon: <CardViewIcon /> },
    { value: 'compact', label: t('hardcoded.pages_sensorspage.compact_view', 'Compacta'), icon: <CompactViewIcon /> }
  ];

  if (sensorsLoading || vehiclesLoading) {
    return <SimpleLoading message={t('sensors.loadingMessage', 'Cargando sensores...')} />;
  }

  // Render content based on view mode
  const renderSensorsContent = () => {
    if (filteredAndSortedSensors.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SensorIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('sensors.noSensorsFound', 'No se encontraron sensores')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchSensors ?
              t('sensors.adjustCriteria', 'Intenta ajustar tus criterios de búsqueda') :
              t('sensors.registerFirst', 'Registra tu primer sensor para comenzar')}
          </Typography>
        </Paper>
      );
    }

    switch (viewMode) {
      case 'cards':
        return (
          <SensorsCardView
            sensors={filteredAndSortedSensors}
            vehicles={vehicles}
            onEdit={hasPermission('manager') ? handleSensorDialog : undefined}
            onDelete={hasPermission('manager') ? handleDelete : undefined}
            t={t}
          />
        );
      case 'compact':
        return (
          <CompactListView
            sensors={filteredAndSortedSensors}
            vehicles={vehicles}
            onEdit={hasPermission('manager') ? handleSensorDialog : undefined}
            onDelete={hasPermission('manager') ? handleDelete : undefined}
            hasPermission={hasPermission}
            t={t}
          />
        );
      default: // table
        return (
          <AdvancedDataTable
            storageKey="sensors_table"
            rows={filteredAndSortedSensors}
            rowKey="id"
            initialSortBy="sensor_id"
            columns={[
              {
                id: 'sensor_id',
                label: t('sensors.table.sensorId', 'ID del Sensor'),
                accessor: (row) => row.sensor_id,
                filterable: true,
                render: (row) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SensorIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <span style={{ fontWeight: 600 }}>{ensureString(row.sensor_id)}</span>
                  </Box>
                )
              },
              {
                id: 'vehicle',
                label: t('sensors.table.assignedVehicle', 'Vehículo Asignado'),
                accessor: (row) => getSensorAssignment(row).vehicleName || t('sensors.stats.unassigned'),
                filterable: true,
                render: (row) => {
                  const assignment = getSensorAssignment(row);
                  return assignment.vehicleName ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VehicleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      <span style={{ color: '#2e7d32', fontWeight: 600 }}>{assignment.vehicleName}</span>
                    </Box>
                  ) : (
                    <Chip label={t('sensors.card.unassigned', 'Sin Asignar')} size="small" color="default" variant="outlined" />
                  );
                }
              },
              {
                id: 'position',
                label: t('sensors.table.position', 'Posición'),
                accessor: (row) => getSensorAssignment(row).position || '',
                filterable: true,
                render: (row) => {
                  const assignment = getSensorAssignment(row);
                  return assignment.position || '-';
                }
              },
              {
                id: 'created_at',
                label: t('sensors.table.creationDate', 'Fecha de Creación'),
                accessor: (row) => row.created_at,
                filterable: true,
                render: (row) => safeFormatDateTime(row.created_at)
              }
            ]}
            rowActions={hasPermission('manager') ? (row) => (
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                <Tooltip title={t('sensors.actions.editSensor', 'Editar Sensor')}>
                  <IconButton size="small" onClick={() => handleSensorDialog(row)}><EditIcon /></IconButton>
                </Tooltip>
                <Tooltip title={t('sensors.actions.deleteSensor', 'Eliminar Sensor')}>
                  <IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon /></IconButton>
                </Tooltip>
              </Box>
            ) : undefined}
            emptyTitle={t('sensors.noSensorsFound', 'No se encontraron sensores')}
            emptyDescription={t('sensors.adjustCriteria', 'Intenta ajustar tus criterios de búsqueda')}
          />
        );
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t('sensors.title', 'Gestión de Sensores')}
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('sensors.search', 'Buscar sensores...')}
            value={searchSensors}
            onChange={(e) => setSearchSensors(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <Tooltip title={t('common.refresh', 'Actualizar')}>
            <IconButton
              onClick={() => {
                refetchSensors();
                refetchVehicles();
              }}
              size="small"
              color="primary"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            {viewModeOptions.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                <Tooltip title={option.label}>
                  {option.icon}
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {hasPermission('manager') && (
          <Button
            data-tutorial-id="add-sensor-btn"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleSensorDialog()}
            size="large"
          >
            {t('sensors.registerSensor', 'Registrar Sensor')}
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }} onClick={() => setAssignmentFilter('all')}>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {sensors?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('sensors.stats.totalSensors', 'Total de Sensores')}
              </Typography>
              {assignmentFilter === 'all' && <Chip size="small" color="primary" label={t('common.active')} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }} onClick={() => setAssignmentFilter('assigned')}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {sensors?.filter(s => {
                  const assignment = getSensorAssignment(s);
                  return assignment.vehicleName;
                }).length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('sensors.stats.assignedSensors', 'Sensores Asignados')}
              </Typography>
              {assignmentFilter === 'assigned' && <Chip size="small" color="success" label={t('common.active')} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }} onClick={() => setAssignmentFilter('unassigned')}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {sensors?.filter(s => {
                  const assignment = getSensorAssignment(s);
                  return !assignment.vehicleName;
                }).length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('sensors.stats.unassigned', 'Sin Asignar')}
              </Typography>
              {assignmentFilter === 'unassigned' && <Chip size="small" color="warning" label={t('common.active')} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {vehicles?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('sensors.stats.activeVehicles', 'Vehículos Activos')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Content */}
      {renderSensorsContent()}

      {/* Sensor Form Dialog - ACTUALIZADO con soporte de asignación */}
      <SensorForm
        open={sensorDialog.open}
        onClose={() => setSensorDialog({ open: false, sensor: null })}
        onSubmit={handleSensorSubmit}
        sensor={sensorDialog.sensor}
        loading={savingSensors || updating}
        error={null}
        vehicles={vehicles || []}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, sensor: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            {t('sensors.actions.confirmDelete', 'Confirmar Eliminación')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('sensors.actions.deleteMessage', '¿Estás seguro de que quieres eliminar este sensor? Esta acción no se puede deshacer.')}
          </Typography>
          {deleteDialog.sensor && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {ensureString(deleteDialog.sensor.sensor_id)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('sensors.card.created', 'Creado')}: {safeFormatDateTime(deleteDialog.sensor.created_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, sensor: null })}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={<DeleteIcon />}
          >
            {t('common.delete', 'Eliminar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SensorsPage;
